import type { Page, Query, Unsubscribe } from './query.ts';

export interface Entity {
  readonly id: string;
}

/**
 * Storage for one collection of one entity type.
 *
 * `create` takes a fully formed entity including its id, because ids are
 * generated by the caller (see domain/id.ts) rather than handed back by the
 * database.
 */
export interface Repository<T extends Entity> {
  get(id: string): Promise<T | null>;
  list(query?: Query): Promise<Page<T>>;
  create(entity: T): Promise<void>;
  /** Partial update. Unknown fields are rejected rather than silently stored. */
  update(id: string, patch: Partial<Omit<T, 'id'>>): Promise<void>;
  delete(id: string): Promise<void>;
  /**
   * Calls back immediately with the current page, then on every change.
   *
   * `onError` is not optional decoration: without it a denied or failed
   * listener is indistinguishable from one that simply has not fired yet, and
   * the UI sits on a loading spinner forever.
   */
  subscribe(
    query: Query | undefined,
    onChange: (page: Page<T>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe;
  subscribeOne(
    id: string,
    onChange: (entity: T | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe;
}

export class NotFoundError extends Error {
  constructor(collection: string, id: string) {
    super(`${collection}/${id} not found`);
    this.name = 'NotFoundError';
  }
}

export class ConflictError extends Error {
  constructor(collection: string, id: string) {
    super(`${collection}/${id} already exists`);
    this.name = 'ConflictError';
  }
}

export class ValidationError extends Error {
  readonly issues: unknown;

  constructor(message: string, issues?: unknown) {
    super(message);
    this.name = 'ValidationError';
    this.issues = issues;
  }
}

/**
 * A guarded write found the document had changed since it was read.
 *
 * Thrown rather than overwriting, so a caller that computed something from a
 * snapshot can recompute from the current state instead of quietly discarding
 * whatever landed in between.
 */
export class ConcurrentModificationError extends Error {
  readonly collection: string;
  readonly id: string;

  constructor(collection: string, id: string) {
    super(
      `${collection}/${id} changed while this write was being prepared. ` +
        'Nothing was written; read it again and retry.',
    );
    this.name = 'ConcurrentModificationError';
    this.collection = collection;
    this.id = id;
  }
}
